import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FileUp, Keyboard, X, ArrowRight } from 'lucide-react';
import { ABF_FACILITY_ENTRY } from '../routes';

/**
 * FR-1.1 — one way in, two lanes.
 *
 * A facility is onboarded from its executed agreement, whether that agreement
 * arrives as a PDF or is typed in. Neither lane is a prerequisite for the other
 * and both end at the same contract review, so the choice here is about what the
 * operator has in front of them, not about what the platform will let them do.
 */

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(3px);
`;

const Panel = styled.div`
  width: min(46rem, 100%);
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.15rem 1.2rem 1.3rem;
  border: 1px solid rgba(56, 189, 248, 0.22);
  border-radius: 14px;
  background: #0b1220;
  box-shadow: 0 24px 60px rgba(2, 6, 23, 0.6);
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  h2 { margin: 0; color: #e2e8f0; font-size: 0.95rem; }
  p { margin: 0.3rem 0 0; max-width: 34rem; color: #94a3b8; font-size: 0.72rem; line-height: 1.5; }
`;

const Close = styled.button`
  display: grid;
  place-items: center;
  width: 1.85rem;
  height: 1.85rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.8);
  color: #94a3b8;
  cursor: pointer;
  &:hover { color: #e2e8f0; border-color: rgba(148, 163, 184, 0.4); }
`;

const Lanes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  margin-top: 1rem;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const Lane = styled.button`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.95rem;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.66);
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
  &:hover, &:focus-visible {
    border-color: rgba(56, 189, 248, 0.5);
    background: rgba(14, 165, 233, 0.09);
    transform: translateY(-1px);
  }
  .icon { display: grid; place-items: center; width: 2.1rem; height: 2.1rem; border-radius: 9px;
    background: rgba(14, 165, 233, 0.14); color: #7dd3fc; }
  strong { color: #e2e8f0; font-size: 0.8rem; }
  span { color: #94a3b8; font-size: 0.69rem; line-height: 1.5; }
  .go { display: inline-flex; align-items: center; gap: 0.3rem; margin-top: 0.15rem;
    color: #7dd3fc; font-size: 0.67rem; font-weight: 800; }
`;

const Foot = styled.p`
  margin: 0.9rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
  color: #64748b;
  font-size: 0.67rem;
  line-height: 1.55;
`;

interface Props {
  onClose: () => void;
}

const NewFacilityLanes: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const go = (path: string) => { onClose(); navigate(path); };

  return (
    <Scrim
      role="dialog"
      aria-modal="true"
      aria-label="Onboard a facility"
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={event => { if (event.key === 'Escape') onClose(); }}
    >
      <Panel>
        <Head>
          <div>
            <h2>Onboard a facility</h2>
            <p>
              Both routes produce the same governed record and the same contract review.
              Choose the one that matches what you have in front of you.
            </p>
          </div>
          <Close type="button" onClick={onClose} aria-label="Close"><X size={14} /></Close>
        </Head>
        <Lanes>
          <Lane type="button" autoFocus onClick={() => go('/middle-office?tab=document-processing')}>
            <span className="icon"><FileUp size={16} /></span>
            <strong>I have the executed agreement</strong>
            <span>
              Upload the PDF. Terms, eligibility rules, covenants, accounts and the payment
              calendar are extracted with their page and clause, and you confirm them in review.
            </span>
            <span className="go">Upload the agreement <ArrowRight size={12} /></span>
          </Lane>
          <Lane type="button" onClick={() => go(ABF_FACILITY_ENTRY)}>
            <span className="icon"><Keyboard size={16} /></span>
            <strong>I will enter the agreement</strong>
            <span>
              Type the facility and its contract terms, each against the clause it comes from.
              No PDF is required, and nothing about the facility is limited by its absence.
            </span>
            <span className="go">Enter the agreement <ArrowRight size={12} /></span>
          </Lane>
        </Lanes>
        <Foot>
          Either way a second person activates the contract before it becomes authority, and
          anything the agreement leaves open is asked for in review rather than assumed.
        </Foot>
      </Panel>
    </Scrim>
  );
};

export default NewFacilityLanes;
